import React, { forwardRef, MutableRefObject, useState } from 'react'
import { PressableProps, View, ViewStyle } from 'react-native'

import styled from 'styled-components/native'

import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { type HoverActionItem, HoverActions } from '../../molecules/hoverActions/HoverActions'

export type HoverActionContainerProps = React.PropsWithChildren<{
  items: HoverActionItem[]
  testID?: string
  actionsTestID?: string
  style?: ViewStyle
  useHoveredBackground?: boolean
  onPress?: () => void
  isHovered?: boolean
  setIsHovered?: (isHovered: boolean) => void
  disableHoverActions?: boolean
  disabled?: boolean
  ariaDescribedBy?: string
  onKeyDown?: PressableProps['onKeyDown']
}>

const disabledStyle = (): ViewStyle => ({ cursor: 'auto' })

const HoverActionsStyled = styled(HoverActions)<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  opacity: isVisible ? 1 : 0,
  position: 'absolute',
  right: theme.spacing['16px'],
  top: '50%',
}))

const Container = styled(PressableOpacity)<{
  hoverIn: boolean
  useHoveredBackground?: boolean
}>(({ theme, hoverIn, useHoveredBackground }) => ({
  backgroundColor:
    hoverIn || useHoveredBackground
      ? theme.colors.tertiaryButtonBackgroundHover
      : theme.colors.tertiaryButtonBackgroundDefault,
  position: 'relative',
  width: '100%',
}))

export const HoverActionContainer = forwardRef<View, HoverActionContainerProps>(
  (
    {
      items,
      testID,
      actionsTestID,
      children,
      style,
      useHoveredBackground,
      onPress,
      isHovered: isHoveredExternal,
      setIsHovered: setIsHoveredExternal,
      disableHoverActions,
      disabled,
      ariaDescribedBy,
      onKeyDown,
    },
    ref,
  ) => {
    const [isHoveredInternal, setIsHoveredInternal] = useState(false)
    const isHovered = isHoveredExternal ?? isHoveredInternal
    const setIsHovered = setIsHoveredExternal ?? setIsHoveredInternal
    const showHoverState = isHovered && !disabled
    return (
      <Container
        pressableRef={ref as MutableRefObject<any>}
        onHoverIn={() => setIsHovered(true)}
        onHoverOut={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
        hoverIn={showHoverState}
        testID={testID}
        style={style}
        useHoveredBackground={useHoveredBackground}
        onPress={disabled ? undefined : onPress}
        accessibilityDisabled={disabled}
        pressedOpacity={disabled ? 1 : undefined}
        styleByState={disabled ? disabledStyle : undefined}
        aria-describedby={ariaDescribedBy}
        onKeyDown={onKeyDown}
      >
        {children}
        {disabled ? null : (
          <HoverActionsStyled
            items={items}
            isVisible={isHovered && !disableHoverActions}
            testID={actionsTestID}
            style={{ transform: [{ translateY: '-50%' }] }}
            onHover={() => setIsHovered(true)}
            onFocus={() => setIsHovered(true)}
            onBlur={() => setIsHovered(false)}
          />
        )}
      </Container>
    )
  },
)
